import { Schema } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';

import { MarkdownParser } from '@lyearn/lygo-core';

import { isInCode } from '../../utils';
import { isMarkdown, sliceSingleNode } from './utils';

const key = new PluginKey('paste-handler-plugin');

export const getPasteHandlerPlugin = (markdownParser: MarkdownParser, schema: Schema<any, any>) => {
  let shiftKey = false;
  return [
    new Plugin({
      key,
      props: {
        handleDOMEvents: {
          keydown: (_, event) => {
            if (event.key === 'Shift') {
              shiftKey = true;
            }
            return false;
          },
          keyup: (_, event) => {
            if (event.key === 'Shift') {
              shiftKey = false;
            }
            return false;
          },
        },
        handlePaste: (view, event: ClipboardEvent) => {
          if (!view.props.editable?.(view.state)) {
            return false;
          }

          // Default behavior if there is nothing on the clipboard or were
          // special pasting with no formatting (Shift held)
          if (!event.clipboardData || shiftKey) {
            return false;
          }

          const text = event.clipboardData.getData('text/plain');
          const html = event.clipboardData.getData('text/html');

          if (isInCode(view.state)) {
            event.preventDefault();

            view.dispatch(view.state.tr.insertText(text));
            return true;
          }

          // If the HTML on the clipboard is from Prosemirror then the best
          // compatability is to just use the HTML parser, regardless of
          // whether it "looks" like Markdown, see: outline/outline#2416
          if (html?.includes('data-pm-slice')) {
            console.log(html);
            return false;
          }

          // If the text on the clipboard looks like Markdown OR there is no
          // html on the clipboard then try to parse content as Markdown
          if (isMarkdown(text)) {
            event.preventDefault();

            // get pasted content as slice
            const paste = markdownParser.parse(text);
            if (!paste) return false;
            const slice = paste.slice(0);
            const tr = view.state.tr;
            let currentPos = view.state.selection.from;

            // If the pasted content is a single paragraph then we loop over
            // its content and insert each node one at a time to allow it to
            // be pasted inline with surrounding content.
            const singleNode = sliceSingleNode(slice);
            if (singleNode?.type === schema.nodes.paragraph) {
              singleNode.forEach((node) => {
                tr.insert(currentPos, node);
                currentPos += node.nodeSize;
              });
            } else {
              singleNode
                ? tr.replaceSelectionWith(singleNode, shiftKey)
                : tr.replaceSelection(slice);
            }

            view.dispatch(tr.scrollIntoView().setMeta('paste', true).setMeta('uiEvent', 'paste'));
            return true;
          }

          // otherwise use the default HTML parser which will handle all paste
          // "from the web" events
          return false;
        },
      },
    }),
  ];
};
